/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import externalLinkIcon from '../../../../assets/icons/externalLinkIcon.svg';
import { getCanvasCourseAudio, getCanvasUserAudio } from '../../../../middleware/api';
import { ParentDataInterface } from '../../../../redux/parentDataSlice';
import TableSkeleton from '../../editorUtilityFunctions/skeletonLoader';
import Search from '../../../utils/typeSearch';
import DropDownComponent from '../../../utils/dropDown';
import '../../../formComponent/links/linkMdodal.scss';
import './audioModal.scss';

interface AudioFile {
  id: number | string;
  display_name?: string;
  filename?: string;
  url: string;
}

interface AudioModalContentInterface {
  handleConfirmState: Function;
  handleSelectedElement: Function;
}

const audioTabs = [
  { key: 'courseAudio', label: 'Course Audio' },
  { key: 'userAudio', label: 'User Audio' },
  { key: 'externalLink', label: 'External Link' },
];

const audioSortingOptions = {
  Recent: 'Recent',
  Ascending: 'Ascending',
  Descending: 'Descending',
};

const ALLOWED_URL_PROTOCOLS = new Set(['http:', 'https:', 'ftp:', 'smtp:']);
const IPV4_PATTERN = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;
const HOSTNAME_LABEL_PATTERN = /^[a-z¡-￿0-9](?:[a-z¡-￿0-9-]*[a-z¡-￿0-9])?$/;
const HOSTNAME_TLD_PATTERN = /^[a-z¡-￿]{2,}$/;

const isPrivateIPv4 = (host: string): boolean => {
  const [a, b] = host.split('.').map(Number);
  return (
    a === 10 ||
    a === 127 ||
    (a === 169 && b === 254) ||
    (a === 192 && b === 168) ||
    (a === 172 && b >= 16 && b <= 31)
  );
};

const isValidIPv4Host = (host: string): boolean => {
  const octetsInRange = host.split('.').every((octet) => Number(octet) <= 255);
  return octetsInRange && !isPrivateIPv4(host);
};

const isValidHostnameHost = (host: string): boolean => {
  const labels = host.split('.');
  const tld = labels[labels.length - 1];
  return (
    labels.length > 1 &&
    HOSTNAME_TLD_PATTERN.test(tld) &&
    labels.slice(0, -1).every((label) => HOSTNAME_LABEL_PATTERN.test(label))
  );
};

const isValidPort = (port: string): boolean =>
  port === '' || (port.length >= 2 && port.length <= 5);

const parseAudioUrl = (url: string): URL | null => {
  try {
    return new URL(url);
  } catch {
    try {
      return new URL(`https://${url}`);
    } catch {
      return null;
    }
  }
};

const isValidAudioUrl = (url: string): boolean => {
  if (/\s/.test(url)) return false;
  const parsed = parseAudioUrl(url);
  if (!parsed || !ALLOWED_URL_PROTOCOLS.has(parsed.protocol) || !isValidPort(parsed.port))
    return false;
  const { hostname } = parsed;
  return IPV4_PATTERN.test(hostname) ? isValidIPv4Host(hostname) : isValidHostnameHost(hostname);
};

const audioName = (file: AudioFile) => file.display_name ?? file.filename ?? '';

const compareAudioNames = (a: AudioFile, b: AudioFile) =>
  audioName(a).trim().toUpperCase() > audioName(b).trim().toUpperCase() ? 1 : -1;

const getSortedAudioFiles = (files: AudioFile[], sorting: string): AudioFile[] => {
  if (sorting === 'Ascending') return [...files].sort(compareAudioNames);
  if (sorting === 'Descending') return [...files].sort(compareAudioNames).reverse();
  return files;
};

export const getVisibleAudioFiles = (files: AudioFile[], searchText: string, sorting: string) => {
  const filtered = files.filter((file) =>
    audioName(file).toLowerCase().includes(searchText.toLowerCase()),
  );
  return getSortedAudioFiles(filtered, sorting);
};

const AudioFileIcon = () => (
  <svg width='34' height='34' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M7.30416 7.71014C7.11239 7.74534 6.97436 7.89991 6.97436 8.07944V11.3108C6.80294 11.2162 6.58987 11.1602 6.35897 11.1602C5.79253 11.1602 5.33333 11.4974 5.33333 11.9134C5.33333 12.3294 5.79253 12.6667 6.35897 12.6667C6.92542 12.6667 7.38462 12.3294 7.38462 11.9134V8.84275L10.2564 8.4383V10.7532C10.085 10.6666 9.87192 10.6152 9.64103 10.6152C9.07458 10.6152 8.61538 10.9243 8.61538 11.3057C8.61538 11.687 9.07458 11.9961 9.64103 11.9961C10.2075 11.9961 10.6667 11.687 10.6667 11.3057V7.71008C10.6667 7.47241 10.4298 7.29417 10.176 7.34078L7.30416 7.71014Z'
      fill='#585858'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M14 4.5V14C14 15.1046 13.1046 16 12 16H4C2.89543 16 2 15.1046 2 14V2C2 0.89543 2.89543 0 4 0H9.5L14 4.5ZM11 4.5C10.1716 4.5 9.5 3.82843 9.5 3V1H4C3.44772 1 3 1.44772 3 2V14C3 14.5523 3.44772 15 4 15H12C12.5523 15 13 14.5523 13 14V4.5H11Z'
      fill='#585858'
    />
  </svg>
);

const parseAudioResponse = (raw: any): AudioFile[] => {
  const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
  const body = data?.body ?? data;
  return Array.isArray(body) ? body : [];
};

export const AudioModalContent = (props: AudioModalContentInterface) => {
  const { handleConfirmState, handleSelectedElement } = props;
  const [activeTab, setActiveTab] = useState(audioTabs[0].key);
  const [audioFiles, setAudioFiles] = useState<AudioFile[]>([]);
  const [selectedAudioId, setSelectedAudioId] = useState<number | string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [externalLinkValue, setExternalLinkValue] = useState('');
  const [isExternalLinkError, setIsExternalLinkError] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isSearchText, setIsSearchText] = useState(false);
  const [selectedSorting, setSelectedSorting] = useState('Recent');
  const dataFromParent = useSelector((state: ParentDataInterface) => state.parentData.data);

  useEffect(() => {
    if (activeTab !== 'courseAudio' && activeTab !== 'userAudio') return;
    const controller = new AbortController();
    const fetchAudio = async () => {
      setIsLoading(true);
      setIsError(false);
      try {
        const raw =
          activeTab === 'courseAudio'
            ? await getCanvasCourseAudio(
                dataFromParent.courseId,
                dataFromParent.env,
                controller.signal,
              )
            : await getCanvasUserAudio(dataFromParent.env, controller.signal);
        setAudioFiles(parseAudioResponse(raw));
      } catch (err) {
        console.log(`error in fetching canvas ${activeTab}`, err);
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAudio();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSelectedAudioId(null);
    setExternalLinkValue('');
    setIsExternalLinkError(false);
    setSearchText('');
    setIsSearchText(false);
    setSelectedSorting('Recent');
    handleSelectedElement({ elementType: '', url: '', fileName: '', name: '', mediaType: '' });
    handleConfirmState(true);
  };

  const handleExternalLinkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setExternalLinkValue(value);
    const trimmedValue = value.trim();
    const url =
      trimmedValue !== '' && !value.startsWith('http://') && !value.startsWith('https://')
        ? `https://${value}`
        : value;
    const hasError = trimmedValue !== '' && !isValidAudioUrl(url);
    setIsExternalLinkError(hasError);
    handleSelectedElement({
      elementType: 'audio',
      mediaType: 'audio',
      url,
      fileName: '',
    });
    handleConfirmState(trimmedValue === '' || hasError);
  };

  const handleSelectAudio = (file: AudioFile) => {
    setSelectedAudioId(file.id);
    handleConfirmState(false);
    handleSelectedElement({
      elementType: 'audio',
      mediaType: 'audio',
      url: file.url,
      fileName: audioName(file),
    });
  };

  const renderList = () => {
    if (isLoading) {
      return <TableSkeleton fromLinksModal />;
    }
    if (isError) {
      return <div>Error in fetching audio files</div>;
    }
    if (audioFiles.length === 0) {
      return (
        <em className='d-flex justify-content-center align-items-center h-100'>
          No audio files found
        </em>
      );
    }
    const visibleFiles = getVisibleAudioFiles(audioFiles, searchText, selectedSorting);
    if (visibleFiles.length === 0) {
      return (
        <em className='d-flex justify-content-center align-items-center h-100'>No results found</em>
      );
    }
    return (
      <div className='audio-modal-list-container d-flex flex-row flex-wrap custom-scrollbar'>
        {visibleFiles.map((file) => (
          <div
            key={file.id}
            className={`audio-modal-content-wrapper py-2 ${
              selectedAudioId === file.id ? 'selected-media' : ''
            }`}
            role='button'
            tabIndex={0}
            title={audioName(file)}
            aria-hidden
            onClick={() => handleSelectAudio(file)}
          >
            <AudioFileIcon />
            <div className='text-truncate w-75 text-center'>{audioName(file)}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className='audio-modal-container'>
      <div className='d-flex justify-content-start align-items-center gap-1'>
        {audioTabs.map((tab) => (
          <button
            key={tab.key}
            type='button'
            aria-pressed={activeTab === tab.key}
            className={`linkTypeLabel ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {(activeTab === 'courseAudio' || activeTab === 'userAudio') && (
        <div className='w-100 audio-file-list mt-2'>
          <div className='d-flex justify-content-end align-items-center mb-2'>
            <Search
              initialvalue={searchText}
              onChange={setSearchText}
              isSearchText={isSearchText}
              setIsSearchText={setIsSearchText}
              loading={isLoading}
            />
            <div className='sorting-dropdown'>
              <DropDownComponent
                optionCollection={audioSortingOptions}
                setSelectedOption={setSelectedSorting}
                selectedOption={selectedSorting}
                className='mx-2 lint-custom-dropdown'
                type='sorting'
              />
            </div>
          </div>
          <div className='listTab'>{renderList()}</div>
        </div>
      )}
      {activeTab === 'externalLink' && (
        <div className='audio-external-link-container mt-3'>
          <Form>
            <div className='external-links-audio-icon'>
              <img src={externalLinkIcon} alt='externalLinks-audio' />
            </div>
            <Form.Control
              className={`external-links-audio-tab-input ${
                isExternalLinkError ? 'invalid-url-state' : ''
              }`}
              autoComplete='off'
              placeholder='External audio link'
              value={externalLinkValue.replace(/ +/g, ' ')}
              onChange={handleExternalLinkChange}
            />
            <div
              id='lint-external-audio-error-message'
              className={`${isExternalLinkError ? '' : 'd-none'} text-danger`}
            >
              <p>Enter a valid Audio url</p>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
};
